import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface MobileBottomNavProps {
  embedded?: boolean;
}

export default function MobileBottomNav({ embedded = false }: MobileBottomNavProps) {
  const location = useLocation();
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const update = () => {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      setCartCount(cart.reduce((s: number, i: { quantity: number }) => s + i.quantity, 0));
    };
    update();
    window.addEventListener('cartUpdated', update);
    return () => window.removeEventListener('cartUpdated', update);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navItems = [
    {
      to: '/',
      icon: 'ri-home-5-line',
      iconActive: 'ri-home-5-fill',
      label: 'Ana Sayfa',
    },
    {
      to: '/kategoriler',
      icon: 'ri-apps-line',
      iconActive: 'ri-apps-fill',
      label: 'Kategoriler',
    },
    {
      to: '/ozel-tasarim',
      icon: null,
      iconActive: null,
      label: 'Tasarım',
      isCTA: true,
    },
    {
      to: '/sepet',
      icon: 'ri-shopping-bag-line',
      iconActive: 'ri-shopping-bag-fill',
      label: 'Sepet',
      badge: cartCount,
    },
  ];

  return (
    <nav className={embedded ? 'bg-white border-t border-gray-100' : 'md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-100'}>
      {/* Safe area for iPhone home indicator */}
      <div className="flex items-center justify-around px-2 pt-2 pb-safe" style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}>
        {navItems.map((item) => {
          const active = isActive(item.to);

          if (item.isCTA) {
            return (
              <Link
                key={item.to}
                to={item.to}
                className="flex flex-col items-center gap-0.5 -mt-5 cursor-pointer"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-orange-500 active:bg-orange-600 transition-all">
                  <i className="ri-edit-2-fill text-white text-2xl"></i>
                </div>
                <span className="text-[10px] font-semibold text-orange-500 mt-0.5 whitespace-nowrap">
                  {item.label}
                </span>
              </Link>
            );
          }

          return (
            <Link
              key={item.to}
              to={item.to}
              className="flex flex-col items-center gap-0.5 py-1 px-3 relative cursor-pointer"
            >
              {/* Active indicator dot */}
              {active && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-orange-500" />
              )}

              {/* Icon */}
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${active ? 'bg-orange-50' : ''}`}>
                <i className={`${active ? item.iconActive : item.icon} text-[22px] transition-colors ${active ? 'text-orange-500' : 'text-gray-400'}`}></i>
                {/* Cart badge */}
                {item.badge != null && item.badge > 0 && (
                  <span className="absolute top-0.5 right-1.5 bg-orange-500 text-white text-[9px] font-bold min-w-[16px] h-4 flex items-center justify-center rounded-full px-1 leading-none">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>

              {/* Label */}
              <span className={`text-[10px] font-semibold whitespace-nowrap transition-colors ${active ? 'text-orange-500' : 'text-gray-400'}`}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
